import { useState, useMemo } from 'react';
import {
  AlertCircle, Building2, ChevronDown, ChevronRight, Edit2,
  Loader2, Package, Phone, Plus, Search, Trash2, X,
} from 'lucide-react';
import { PageShell } from '@/components/layout/PageShell';
import { EmptyState } from '@/components/ui/EmptyState';
import { CurrencyInput } from '@/components/ui/CurrencyInput';
import {
  useSuppliers,
  UNIT_LABELS, RECURRENCE_LABELS,
  type Supplier, type SupplierInput, type SupplierProductInput,
  type UnitType, type Recurrence,
} from '@/hooks/useSuppliers';
import { formatBRL } from '@/lib/format';
import { cn } from '@/lib/utils';

// ─── Helpers ──────────────────────────────────────────────────────────────────

const UNIT_TYPES  = Object.keys(UNIT_LABELS)  as UnitType[];
const RECURRENCES = Object.keys(RECURRENCE_LABELS) as Recurrence[];

const RECURRENCE_CHIP: Record<Recurrence, string> = {
  semanal:     'chip-negative',
  quinzenal:   'chip-warning',
  mensal:      'chip-info',
  trimestral:  'chip-neutral',
  semestral:   'chip-neutral',
  anual:       'chip-neutral',
  sob_demanda: 'chip-positive',
};

function emptyProduct(): SupplierProductInput {
  return {
    product_name: '', unit_type: 'unidade', unit_price_cents: 0,
    units_per_purchase: 1, recurrence: 'mensal',
    last_purchase_date: '', notes: '',
  };
}

const EMPTY_INPUT: SupplierInput = {
  name: '', document: '', email: '', phone: '', website: '', notes: '',
  products: [emptyProduct()],
};

// ─── Modal de Fornecedor ──────────────────────────────────────────────────────

function SupplierModal({
  initial,
  onSave,
  onClose,
}: {
  initial?: Supplier;
  onSave: (data: SupplierInput) => Promise<void>;
  onClose: () => void;
}) {
  const [form, setForm] = useState<SupplierInput>(
    initial
      ? {
          name:     initial.name,
          document: initial.document ?? '',
          email:    initial.meta.email    ?? '',
          phone:    initial.meta.phone    ?? '',
          website:  initial.meta.website  ?? '',
          notes:    initial.meta.notes    ?? '',
          products: initial.products.length > 0
            ? initial.products.map((p) => ({
                id:                 p.id,
                product_name:       p.product_name,
                unit_type:          p.unit_type,
                unit_price_cents:   p.unit_price_cents,
                units_per_purchase: p.units_per_purchase,
                recurrence:         p.recurrence,
                last_purchase_date: p.last_purchase_date ?? '',
                notes:              p.notes ?? '',
              }))
            : [emptyProduct()],
        }
      : { ...EMPTY_INPUT, products: [emptyProduct()] }
  );
  const [saving, setSaving] = useState(false);
  const [saveErr, setSaveErr] = useState('');
  const [nameErr, setNameErr] = useState('');

  function setField<K extends keyof SupplierInput>(key: K, value: SupplierInput[K]) {
    setForm((f) => ({ ...f, [key]: value }));
  }

  function setProduct(idx: number, key: keyof SupplierProductInput, value: string | number) {
    setForm((f) => {
      const products = [...f.products];
      products[idx] = { ...products[idx], [key]: value } as SupplierProductInput;
      return { ...f, products };
    });
  }

  function addProduct() {
    setForm((f) => ({ ...f, products: [...f.products, emptyProduct()] }));
  }

  function removeProduct(idx: number) {
    setForm((f) => ({ ...f, products: f.products.filter((_, i) => i !== idx) }));
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setNameErr('');
    setSaveErr('');

    if (!form.name.trim()) { setNameErr('Nome é obrigatório.'); return; }

    setSaving(true);
    try {
      await onSave(form);
      onClose();
    } catch (err: any) {
      setSaveErr(err.message ?? 'Erro ao salvar fornecedor.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm"
      onClick={(e) => { if (e.target === e.currentTarget && !saving) onClose(); }}
    >
      <form
        onSubmit={handleSubmit}
        className="bg-surface-raised rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] flex flex-col"
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-border">
          <h2 className="font-display font-bold text-lg text-ink">
            {initial ? 'Editar fornecedor' : 'Novo fornecedor'}
          </h2>
          <button type="button" onClick={onClose} disabled={saving} className="btn-ghost !p-1.5">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="overflow-y-auto flex-1 p-6 space-y-6">
          {/* Dados do fornecedor */}
          <section>
            <h3 className="text-xs font-semibold text-ink-muted uppercase tracking-wider mb-3">
              Dados do fornecedor
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="sm:col-span-2">
                <label className="block text-xs font-medium text-ink-muted mb-1">
                  Nome <span className="text-negative">*</span>
                </label>
                <input
                  className={cn('input w-full', nameErr && 'border-negative')}
                  placeholder="Razão social ou nome fantasia"
                  value={form.name}
                  onChange={(e) => { setField('name', e.target.value); setNameErr(''); }}
                />
                {nameErr && <p className="text-xs text-negative mt-1">{nameErr}</p>}
              </div>

              <div>
                <label className="block text-xs font-medium text-ink-muted mb-1">CNPJ / CPF</label>
                <input
                  className="input w-full"
                  placeholder="00.000.000/0001-00"
                  value={form.document}
                  onChange={(e) => setField('document', e.target.value)}
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-ink-muted mb-1">Telefone / WhatsApp</label>
                <input
                  className="input w-full"
                  placeholder="(11) 99999-9999"
                  value={form.phone}
                  onChange={(e) => setField('phone', e.target.value)}
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-ink-muted mb-1">E-mail</label>
                <input
                  type="email"
                  className="input w-full"
                  placeholder="contato@fornecedor.com.br"
                  value={form.email}
                  onChange={(e) => setField('email', e.target.value)}
                />
              </div>

              <div>
                <label className="block text-xs font-medium text-ink-muted mb-1">Website</label>
                <input
                  className="input w-full"
                  placeholder="https://fornecedor.com.br"
                  value={form.website}
                  onChange={(e) => setField('website', e.target.value)}
                />
              </div>

              <div className="sm:col-span-2">
                <label className="block text-xs font-medium text-ink-muted mb-1">Observações</label>
                <textarea
                  className="input w-full resize-none"
                  rows={2}
                  placeholder="Condições de pagamento, prazo de entrega, contato principal…"
                  value={form.notes}
                  onChange={(e) => setField('notes', e.target.value)}
                />
              </div>
            </div>
          </section>

          {/* Produtos */}
          <section>
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-xs font-semibold text-ink-muted uppercase tracking-wider">
                Produtos adquiridos
              </h3>
              <button
                type="button"
                onClick={addProduct}
                className="text-xs text-brand-700 font-medium hover:underline flex items-center gap-1"
              >
                <Plus className="w-3.5 h-3.5" /> Adicionar produto
              </button>
            </div>

            <div className="space-y-3">
              {form.products.map((prod, idx) => (
                <div key={idx} className="rounded-xl border border-border bg-surface-subtle p-3 space-y-3">
                  {/* Linha 1: nome + tipo + qtd */}
                  <div className="grid grid-cols-12 gap-2 items-end">
                    <div className="col-span-12 sm:col-span-5">
                      <label className="block text-[11px] font-medium text-ink-muted mb-1">Produto</label>
                      <input
                        className="input w-full text-sm"
                        placeholder="Ex: Plástico bolha, Fita adesiva, Caixa…"
                        value={prod.product_name}
                        onChange={(e) => setProduct(idx, 'product_name', e.target.value)}
                      />
                    </div>

                    <div className="col-span-6 sm:col-span-3">
                      <label className="block text-[11px] font-medium text-ink-muted mb-1">Tipo de unidade</label>
                      <select
                        className="input w-full text-sm"
                        value={prod.unit_type}
                        onChange={(e) => setProduct(idx, 'unit_type', e.target.value as UnitType)}
                      >
                        {UNIT_TYPES.map((u) => (
                          <option key={u} value={u}>{UNIT_LABELS[u]}</option>
                        ))}
                      </select>
                    </div>

                    <div className="col-span-5 sm:col-span-3">
                      <label className="block text-[11px] font-medium text-ink-muted mb-1">Qtd por compra</label>
                      <input
                        type="text"
                        inputMode="decimal"
                        className="input w-full text-sm"
                        placeholder="Ex: 50"
                        value={prod.units_per_purchase === 0 ? '' : String(prod.units_per_purchase).replace('.', ',')}
                        onChange={(e) => {
                          const raw = e.target.value.replace(/[^\d.,]/g, '').replace(',', '.');
                          setProduct(idx, 'units_per_purchase', parseFloat(raw) || 0);
                        }}
                      />
                    </div>

                    <div className="col-span-1 flex justify-end">
                      {form.products.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeProduct(idx)}
                          className="text-ink-soft hover:text-negative transition-colors mt-5"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Linha 2: preço + recorrência + última compra */}
                  <div className="grid grid-cols-12 gap-2 items-end">
                    <div className="col-span-6 sm:col-span-3">
                      <label className="block text-[11px] font-medium text-ink-muted mb-1">Preço unitário (R$)</label>
                      <CurrencyInput
                        className="input w-full text-sm"
                        value={prod.unit_price_cents}
                        onChange={(cents) => setProduct(idx, 'unit_price_cents', cents)}
                      />
                    </div>

                    <div className="col-span-6 sm:col-span-3">
                      <label className="block text-[11px] font-medium text-ink-muted mb-1">Recorrência</label>
                      <select
                        className="input w-full text-sm"
                        value={prod.recurrence}
                        onChange={(e) => setProduct(idx, 'recurrence', e.target.value as Recurrence)}
                      >
                        {RECURRENCES.map((r) => (
                          <option key={r} value={r}>{RECURRENCE_LABELS[r]}</option>
                        ))}
                      </select>
                    </div>

                    <div className="col-span-12 sm:col-span-3">
                      <label className="block text-[11px] font-medium text-ink-muted mb-1">Última compra</label>
                      <input
                        type="date"
                        className="input w-full text-sm"
                        value={prod.last_purchase_date}
                        onChange={(e) => setProduct(idx, 'last_purchase_date', e.target.value)}
                      />
                    </div>

                    <div className="col-span-12 sm:col-span-3">
                      <label className="block text-[11px] font-medium text-ink-muted mb-1">Obs. do produto</label>
                      <input
                        className="input w-full text-sm"
                        placeholder="Ex: Cor, especificação…"
                        value={prod.notes}
                        onChange={(e) => setProduct(idx, 'notes', e.target.value)}
                      />
                    </div>
                  </div>

                  {/* Resumo do custo */}
                  {prod.unit_price_cents > 0 && prod.units_per_purchase > 0 && (
                    <p className="text-[11px] text-ink-muted">
                      Custo estimado por compra:{' '}
                      <span className="font-semibold text-ink">
                        {formatBRL(prod.unit_price_cents * prod.units_per_purchase)}
                      </span>{' '}
                      ({prod.units_per_purchase} {UNIT_LABELS[prod.unit_type].toLowerCase()} × {formatBRL(prod.unit_price_cents)})
                    </p>
                  )}
                </div>
              ))}
            </div>
          </section>

          {saveErr && (
            <div className="flex items-start gap-2 rounded-lg bg-negative/10 border border-negative/20 px-3 py-2 text-sm text-negative">
              <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
              {saveErr}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-border flex justify-end gap-3">
          <button type="button" onClick={onClose} disabled={saving} className="btn-secondary">
            Cancelar
          </button>
          <button type="submit" disabled={saving} className="btn-primary gap-2">
            {saving && <Loader2 className="w-4 h-4 animate-spin" />}
            {saving ? 'Salvando…' : initial ? 'Salvar alterações' : 'Cadastrar fornecedor'}
          </button>
        </div>
      </form>
    </div>
  );
}

// ─── Card de fornecedor ───────────────────────────────────────────────────────

function SupplierCard({
  supplier,
  onEdit,
  onDelete,
  confirmDeleteId,
  setConfirmDeleteId,
}: {
  supplier: Supplier;
  onEdit: (s: Supplier) => void;
  onDelete: (id: string) => void;
  confirmDeleteId: string | null;
  setConfirmDeleteId: (id: string | null) => void;
}) {
  const [expanded, setExpanded] = useState(false);

  const totalCostPerCycle = supplier.products.reduce(
    (sum, p) => sum + p.unit_price_cents * p.units_per_purchase,
    0
  );

  return (
    <div className="card">
      {/* Header do card */}
      <div className="flex items-center gap-3 p-4">
        <div className="w-10 h-10 rounded-xl bg-brand-50 flex items-center justify-center shrink-0">
          <Building2 className="w-5 h-5 text-brand-700" />
        </div>

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <p className="font-semibold text-ink truncate">{supplier.name}</p>
            {supplier.document && (
              <span className="text-[11px] text-ink-soft font-mono">{supplier.document}</span>
            )}
          </div>
          <div className="flex items-center gap-3 mt-0.5 text-xs text-ink-muted">
            {supplier.meta.phone && (
              <span className="flex items-center gap-1">
                <Phone className="w-3 h-3" />{supplier.meta.phone}
              </span>
            )}
            {supplier.meta.email && <span>{supplier.meta.email}</span>}
          </div>
        </div>

        <div className="flex items-center gap-2 shrink-0">
          <div className="text-right hidden sm:block">
            <p className="text-xs text-ink-muted">{supplier.products.length} produto{supplier.products.length !== 1 ? 's' : ''}</p>
            {totalCostPerCycle > 0 && (
              <p className="text-xs font-semibold text-ink">{formatBRL(totalCostPerCycle)}/ciclo</p>
            )}
          </div>

          <button
            onClick={() => onEdit(supplier)}
            className="btn-ghost !p-1.5 text-ink-muted hover:text-ink"
          >
            <Edit2 className="w-4 h-4" />
          </button>

          {confirmDeleteId === supplier.id ? (
            <div className="flex items-center gap-1">
              <button
                onClick={() => onDelete(supplier.id)}
                className="text-xs font-medium text-negative hover:underline px-2 py-1 rounded-lg hover:bg-negative/10"
              >
                Confirmar
              </button>
              <button
                onClick={() => setConfirmDeleteId(null)}
                className="btn-ghost !p-1"
              >
                <X className="w-3.5 h-3.5" />
              </button>
            </div>
          ) : (
            <button
              onClick={() => setConfirmDeleteId(supplier.id)}
              className="btn-ghost !p-1.5 text-ink-soft hover:text-negative"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          )}

          {supplier.products.length > 0 && (
            <button
              onClick={() => setExpanded((v) => !v)}
              className="btn-ghost !p-1.5 text-ink-muted"
            >
              {expanded ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            </button>
          )}
        </div>
      </div>

      {/* Produtos expandidos */}
      {expanded && supplier.products.length > 0 && (
        <div className="border-t border-border">
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[640px]">
              <thead>
                <tr className="bg-surface-subtle">
                  <th className="px-4 py-2.5 text-left text-xs font-medium text-ink-muted">Produto</th>
                  <th className="px-4 py-2.5 text-left text-xs font-medium text-ink-muted">Unidade</th>
                  <th className="px-4 py-2.5 text-right text-xs font-medium text-ink-muted">Preço unit.</th>
                  <th className="px-4 py-2.5 text-right text-xs font-medium text-ink-muted">Qtd/compra</th>
                  <th className="px-4 py-2.5 text-right text-xs font-medium text-ink-muted">Custo/compra</th>
                  <th className="px-4 py-2.5 text-center text-xs font-medium text-ink-muted">Recorrência</th>
                  <th className="px-4 py-2.5 text-center text-xs font-medium text-ink-muted">Última compra</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {supplier.products.map((p) => (
                  <tr key={p.id} className="hover:bg-surface-subtle/50 transition-colors">
                    <td className="px-4 py-2.5 font-medium text-ink">
                      {p.product_name}
                      {p.notes && <span className="ml-1 text-ink-soft text-xs">— {p.notes}</span>}
                    </td>
                    <td className="px-4 py-2.5 text-ink-muted">{UNIT_LABELS[p.unit_type]}</td>
                    <td className="px-4 py-2.5 text-right tabular-nums">{formatBRL(p.unit_price_cents)}</td>
                    <td className="px-4 py-2.5 text-right tabular-nums text-ink-muted">
                      {p.units_per_purchase % 1 === 0
                        ? p.units_per_purchase.toFixed(0)
                        : p.units_per_purchase.toFixed(3).replace(/0+$/, '')}
                    </td>
                    <td className="px-4 py-2.5 text-right tabular-nums font-semibold text-ink">
                      {formatBRL(p.unit_price_cents * p.units_per_purchase)}
                    </td>
                    <td className="px-4 py-2.5 text-center">
                      <span className={cn('chip', RECURRENCE_CHIP[p.recurrence])}>
                        {RECURRENCE_LABELS[p.recurrence]}
                      </span>
                    </td>
                    <td className="px-4 py-2.5 text-center text-ink-muted text-xs">
                      {p.last_purchase_date
                        ? new Date(p.last_purchase_date + 'T00:00:00').toLocaleDateString('pt-BR')
                        : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Observações do fornecedor */}
      {expanded && supplier.meta.notes && (
        <div className="px-4 py-3 border-t border-border bg-surface-subtle text-xs text-ink-muted">
          <span className="font-medium text-ink">Obs: </span>{supplier.meta.notes}
        </div>
      )}
    </div>
  );
}

// ─── Página principal ─────────────────────────────────────────────────────────

export function Suppliers() {
  const { suppliers, loading, fetchError, addSupplier, updateSupplier, deleteSupplier } = useSuppliers();

  const [search, setSearch]               = useState('');
  const [modalOpen, setModalOpen]         = useState(false);
  const [editing, setEditing]             = useState<Supplier | null>(null);
  const [actionError, setActionError]     = useState<string | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return q
      ? suppliers.filter(
          (s) =>
            s.name.toLowerCase().includes(q) ||
            (s.document ?? '').includes(q) ||
            s.products.some((p) => p.product_name.toLowerCase().includes(q))
        )
      : suppliers;
  }, [suppliers, search]);

  const totalProducts = suppliers.reduce((sum, s) => sum + s.products.length, 0);
  const totalCostCycle = suppliers.reduce(
    (sum, s) => sum + s.products.reduce((ps, p) => ps + p.unit_price_cents * p.units_per_purchase, 0),
    0
  );

  function openAdd() {
    setEditing(null);
    setModalOpen(true);
  }

  function openEdit(s: Supplier) {
    setEditing(s);
    setModalOpen(true);
  }

  async function handleSave(data: SupplierInput) {
    if (editing) {
      await updateSupplier(editing.id, data);
    } else {
      await addSupplier(data);
    }
  }

  async function handleDelete(id: string) {
    setActionError(null);
    try {
      await deleteSupplier(id);
      setConfirmDeleteId(null);
    } catch (err: any) {
      setActionError(err.message ?? 'Erro ao excluir fornecedor.');
      setConfirmDeleteId(null);
    }
  }

  return (
    <PageShell
      title="Fornecedores"
      subtitle="Gerencie seus fornecedores e os produtos que você adquire de cada um."
    >
      {/* Stats */}
      {!loading && suppliers.length > 0 && (
        <div className="grid grid-cols-3 gap-3 mb-6">
          {[
            { label: 'Fornecedores',  value: suppliers.length,  unit: '' },
            { label: 'Produtos / SKUs', value: totalProducts, unit: '' },
            { label: 'Custo/ciclo',   value: formatBRL(totalCostCycle), unit: '' },
          ].map((stat) => (
            <div key={stat.label} className="card p-4 text-center">
              <p className="stat-label">{stat.label}</p>
              <p className="text-xl font-bold text-ink mt-0.5">{stat.value}{stat.unit}</p>
            </div>
          ))}
        </div>
      )}

      {/* Toolbar */}
      <div className="flex gap-3 mb-4 flex-wrap">
        <div className="relative flex-1 min-w-48">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-ink-soft" />
          <input
            type="search"
            placeholder="Buscar por nome, CNPJ ou produto…"
            className="input w-full pl-9"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <button onClick={openAdd} className="btn-primary gap-2 shrink-0">
          <Plus className="w-4 h-4" /> Novo fornecedor
        </button>
      </div>

      {/* Erro de ação */}
      {actionError && (
        <div className="flex items-center justify-between gap-2 mb-4 rounded-lg bg-negative/10 border border-negative/20 px-3 py-2 text-sm text-negative">
          <div className="flex items-center gap-2">
            <AlertCircle className="w-4 h-4 shrink-0" />{actionError}
          </div>
          <button onClick={() => setActionError(null)}><X className="w-4 h-4" /></button>
        </div>
      )}

      {/* Conteúdo */}
      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <div key={i} className="h-20 rounded-2xl bg-surface-subtle animate-pulse" />
          ))}
        </div>
      ) : fetchError ? (
        <div className="card p-6 flex items-start gap-3 text-negative">
          <AlertCircle className="w-5 h-5 shrink-0 mt-0.5" />
          <div>
            <p className="font-medium text-sm">Erro ao carregar fornecedores</p>
            <p className="text-xs mt-1 text-ink-muted">{fetchError}</p>
          </div>
        </div>
      ) : filtered.length === 0 ? (
        <div className="card p-8">
          <EmptyState
            icon={Package}
            title={search ? 'Nenhum fornecedor encontrado' : 'Nenhum fornecedor cadastrado'}
            description={
              search
                ? 'Tente outro termo de busca.'
                : 'Cadastre fornecedores para controlar preços, recorrência de compras e custo de insumos.'
            }
            action={!search ? (
              <button onClick={openAdd} className="btn-primary gap-2">
                <Plus className="w-4 h-4" /> Cadastrar fornecedor
              </button>
            ) : undefined}
          />
        </div>
      ) : (
        <div className="space-y-3">
          {filtered.map((s) => (
            <SupplierCard
              key={s.id}
              supplier={s}
              onEdit={openEdit}
              onDelete={handleDelete}
              confirmDeleteId={confirmDeleteId}
              setConfirmDeleteId={setConfirmDeleteId}
            />
          ))}
        </div>
      )}

      {/* Modal */}
      {modalOpen && (
        <SupplierModal
          initial={editing ?? undefined}
          onSave={handleSave}
          onClose={() => setModalOpen(false)}
        />
      )}
    </PageShell>
  );
}
